import React, { useEffect, useRef, useCallback, useState } from "react";
import { LockClosedIcon } from "@heroicons/react/24/solid";
import { CanopyInsuranceConnectProps } from "./types";
import { CheckIcon } from "./Icons";

declare global {
  interface Window {
    CanopyConnect: any;
  }
}

const CanopyInsuranceConnect: React.FC<CanopyInsuranceConnectProps> = ({
  publicAlias,
  userId,
  isLoader,
  isConnected,
  onSuccess,
  onError,
}) => {
  const handlerRef = useRef<any>(null);

  useEffect(() => {
    let isMounted = true;

    const init = async () => {
      try {
        if (!publicAlias) return;

        if (!window.CanopyConnect) {
          await loadCanopySdk();
        }

        if (!isMounted) return;

        handlerRef.current?.destroy?.();

        const canopyHandler = window.CanopyConnect.create({
          publicAlias,
          pullMetaData: { userId },
        });

        canopyHandler.on("exit", (err: any, data: any) => {
          if (err === null && data?.pull !== null) {
            onSuccess?.(data);
          }
        });

        canopyHandler.on("error", (err: any) => {
          onError?.(err);
        });

        handlerRef.current = canopyHandler;
      } catch (err: any) {
        onError?.(err);
      }
    };

    init();

    return () => {
      isMounted = false;
      handlerRef.current?.destroy?.();
      handlerRef.current = null;
    };
  }, [publicAlias, userId]);

  const handleConnect = () => {
    handlerRef.current?.open();
  };

  return (
    <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-6 text-center">
      <h3 className="text-lg lg:text-2xl font-semibold text-[#09517d]">
        Connect Insurance Instantly
      </h3>

      <p className="mt-2 text-sm lg:text-lg text-gray-600">
        Securely verify your insurance in seconds—no uploads required.
      </p>

      <button
        onClick={handleConnect}
        disabled={isLoader || isConnected}
        className={` ${
          isConnected
            ? "bg-green-700 hover:bg-green-700 disabled:bg-green-600"
            : "bg-[#09517d] hover:bg-[#084a6f] disabled:opacity-50 "
        } mt-4 w-full rounded-md px-6 py-3 text-white text-base lg:text-xl font-medium transition-colors  disabled:cursor-not-allowed`}
      >
        {isConnected ? (
          <div className="flex items-center justify-center gap-1">
            <CheckIcon className="w-5 h-5 text-white flex-shrink-0" /> Insurance
            Connected
          </div>
        ) : (
          "Connect Insurance"
        )}
      </button>

      <div className="mt-3 flex items-center justify-center text-sm lg:text-base text-gray-600">
        <LockClosedIcon className="mr-2 h-4 w-4 lg:h-5 lg:w-5" />
        Secure Connection
      </div>
    </div>
  );
};

export default CanopyInsuranceConnect;

function loadCanopySdk(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.CanopyConnect) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.src = "https://cdn.usecanopy.com/v2/canopy-connect.js";
    script.async = true;

    script.onload = () => {
      if (window.CanopyConnect) {
        resolve();
      } else {
        reject(new Error("Canopy SDK loaded but not available"));
      }
    };

    script.onerror = () => reject(new Error("Failed to load Canopy SDK"));
    document.body.appendChild(script);
  });
}
